import React from 'react';
import { SpaceBetweenShowInfo, StyledContainer } from '../../../components/container/styles';
import { TextLarge } from '../../../components/text';
import { CardTheme } from '../../../components/card';
import { ResponsivePageLayout } from '../../../components/page/responsive-layout/ResponsivePageLayout';
import { Icon } from '../../../resources/icon';
import ButtonExcelEmbedded from '../../../components/report-excel/ButtonExcelEmbedded';
import { ButtonTheme, Gap } from '../../../components';
import { columns, exportHeaders } from './columns';
import { ChangeTrackNumber } from './modals/TrackingNumber';
import { LineMessage } from './modals/LineMessage';
import { RemarkMessage } from './modals/RemarkMessage';
import { InfoClaimStatus } from './views/InfoRegistration';
import { Row, Select, Spin } from 'antd';
import { useGetClaimList } from './hooks/useGetClaimList';
import { CustomImage } from '../../../functions/Image';
import { searchDefaultValues } from './form';
import { ProcessStatus } from './modals/ProcessStatus';
import { useStockAvailability } from './hooks/useStockAvailability';
import { dropOverSelectedKeys, getRowStockState, summarizeSelection } from './func/stockHelper';
import { STOCK_ROW_CLASS } from './resource/stockConst';
import { StockSelectionAlert } from './views/StockSelectionAlert';
import { StyledStockTable } from './styles';
import { CLAIM_STATUS, PROCESS_STATUS } from '../../../resources/status';
import { STOCK_ROW_STATE } from './resource/stockConst';
import { filterClaimRows } from './func/filterClaimRows';

const stockFilterOptions = [
  { value: STOCK_ROW_STATE.OK, label: 'สต็อกเพียงพอ' },
  { value: STOCK_ROW_STATE.OUT_OF_STOCK, label: 'สต็อกไม่พอ' },
  { value: STOCK_ROW_STATE.OVER_SELECTED, label: 'เลือกเกินสต็อก' },
  { value: STOCK_ROW_STATE.UNKNOWN, label: 'ไม่มีข้อมูลสต็อก' },
  { value: STOCK_ROW_STATE.STALE, label: 'ข้อมูลสต็อกเก่า' },
  { value: STOCK_ROW_STATE.EXCLUDED, label: 'ไม่คิดสต็อก (จัดส่งแล้ว)' },
];

export const ClaimList = () => {
  const [claimStatusFilter, setClaimStatusFilter] = React.useState();
  const [processStatusFilter, setProcessStatusFilter] = React.useState();
  const [stockStateFilter, setStockStateFilter] = React.useState();
  const {
    formSearch,
    onOpenClaimAdd,
    onOpenTracking,
    onOpenLineMessage,
    onOpenRemarkMessage,
    onToggleTracking,
    onToggleLineMessage,
    onToggleRemarkMessage,
    onSearchByField,
    handleSubmitClaim,
    loadingClaimDate,
    loadingClaimFull,
    dataClaimDate,
    dataClaimFull,
    selectedRowKeys,
    searchType,
    claimData,
    onFetch,
    onSelectChange,
    onOpenClaimView,
    onOpenClaimEdit,
    formSearchObj,
    openLineMessage,
    openRemarkMessage,
    openTracking,
    setSelectedRowKeys,
    transformDataForExcel,
    onDownloadClaimPDF,
    onModalProcessStatus,
    openProcessStatus,
    onToggleProcessStatus,
    onFetchClaimFull,
    openConfirmModal,
    loadingSubmitApproveClaim,
    loadingDownloadPDF,
    handleSubmitResendKerry,
    loadingResendKerry,
    privilegeFocusType,
  } = useGetClaimList();

  const tableDataSource = searchType === 'search' ? dataClaimFull : dataClaimDate;
  const {
    stockMap,
    meta: stockMeta,
    unavailable: isStockUnavailable,
    onFetchStock,
  } = useStockAvailability(tableDataSource);

  // สต็อกเป็นข้อมูลประกอบอย่างเดียว ไม่ล็อกการทำงาน
  // ผู้ใช้กดเลือก เปลี่ยนสถานะ และอนุมัติได้เสมอ แม้ของไม่พอ
  const { selectedByItem, overItems, overRowKeys } = summarizeSelection({
    dataSource: tableDataSource,
    selectedRowKeys,
    stockMap,
  });

  const filteredDataSource = filterClaimRows({
    rows: tableDataSource,
    claimStatus: claimStatusFilter,
    processStatus: processStatusFilter,
    stockState: stockStateFilter,
    stockMap,
    selectedByItem,
  });

  const onRemoveOverSelected = () => {
    setSelectedRowKeys((prev) => dropOverSelectedKeys(prev, overRowKeys));
  };

  return (
    <Spin spinning={loadingSubmitApproveClaim || loadingDownloadPDF || loadingResendKerry}>
      <StyledContainer>
        <TextLarge text="การเคลมสินค้า" />
        <CardTheme>
          {privilegeFocusType !== 'READ' && (
            <Row gutter={16} flex justify="space-between">
              <Row justify="start">
                <ButtonTheme
                  useFor="BILL"
                  title="ใบส่งสินค้า"
                  disabled={selectedRowKeys.length === 0}
                  onClick={onDownloadClaimPDF}
                />
                <ButtonTheme
                  useFor="NEW_KERRY"
                  title="ขอเลขพัสดุใหม่"
                  onClick={handleSubmitResendKerry}
                  disabled={selectedRowKeys.length === 0}
                />
              </Row>
              <Row justify="end">
                <ButtonTheme
                  useFor="CONFIRM_OK"
                  title="อนุมัติเคลม"
                  onClick={() =>
                    openConfirmModal({
                      onOk: () => handleSubmitClaim(),
                    })
                  }
                  disabled={selectedRowKeys.length === 0}
                />
                <ButtonTheme useFor="WARRANTY" title="ยื่นเรื่องเคลม" onClick={onOpenClaimAdd} />
              </Row>
            </Row>
          )}
          <Gap />
          <StyledStockTable>
            <ResponsivePageLayout
              searchLayout={{
                title: 'ค้นหาการเคลมสินค้า',
                children: (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, width: '100%', margin: '16px 0' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <label htmlFor="claim-status-filter">สถานะ</label>
                      <Select
                        id="claim-status-filter"
                        aria-label="กรองสถานะ"
                        placeholder="สถานะทั้งหมด"
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        value={claimStatusFilter}
                        onChange={setClaimStatusFilter}
                        options={CLAIM_STATUS.map(({ key, label }) => ({ value: key, label }))}
                        style={{ width: 220, maxWidth: '100%' }}
                      />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <label htmlFor="claim-process-status-filter">สถานะภายใน</label>
                      <Select
                        id="claim-process-status-filter"
                        aria-label="กรองสถานะภายใน"
                        placeholder="สถานะภายในทั้งหมด"
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        value={processStatusFilter}
                        onChange={setProcessStatusFilter}
                        options={PROCESS_STATUS.map(({ key, label }) => ({ value: key, label }))}
                        style={{ width: 240, maxWidth: '100%' }}
                      />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <label htmlFor="claim-stock-filter">สต็อก</label>
                      <Select
                        id="claim-stock-filter"
                        aria-label="กรองสต็อก"
                        placeholder="สต็อกทั้งหมด"
                        allowClear
                        value={stockStateFilter}
                        onChange={setStockStateFilter}
                        options={stockFilterOptions}
                        style={{ width: 240, maxWidth: '100%' }}
                      />
                    </div>
                  </div>
                ),

                icon: <Icon.todo />,
                formSearch,
                useFormParam: formSearchObj,
                onSearch: formSearchObj.handleSubmit(onSearchByField),
                onClearCustom: () => {
                  formSearchObj.reset(searchDefaultValues);
                  setClaimStatusFilter(undefined);
                  setProcessStatusFilter(undefined);
                  setStockStateFilter(undefined);
                  setSelectedRowKeys([]);
                },
              }}
              content={<InfoClaimStatus />}
              tableLayout={{
                columns: columns({
                  onOpenTracking,
                  onOpenLineMessage,
                  onOpenRemarkMessage,
                  onOpenClaimEdit,
                  onOpenClaimView,
                  onModalProcessStatus,
                  privilegeFocusType,
                  stockMap,
                  selectedByItem,
                }),
                loading: loadingClaimFull || loadingClaimDate,
                dataSource: filteredDataSource,
                rowKey: 'claimId',
                rowClassName: (record) => {
                  const { state } = getRowStockState({ record, stockMap, selectedByItem });
                  return STOCK_ROW_CLASS[state] ?? '';
                },
                hasPagination: true,
                rowSelection: {
                  selectedRowKeys,
                  onChange: onSelectChange,
                },
                expandable: {
                  expandedRowRender: (record) => (
                    <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                      <CustomImage
                        width={80}
                        preview
                        src={record?.claimImageUrl}
                        style={{ borderRadius: 10 }}
                      />
                      <CustomImage
                        width={80}
                        preview
                        src={record?.receiptImageUrl}
                        style={{ borderRadius: 10, marginLeft: 10 }}
                      />
                      <div style={{ display: 'flex', flexDirection: 'column', marginLeft: '20px' }}>
                        <div style={{ marginBottom: '8px' }}>
                          <strong>ชื่อสินค้า: </strong>
                          {record.productName}
                        </div>
                        <div style={{ marginBottom: '8px' }}>
                          <strong>รุ่นมือถือ: </strong>
                          {record.mobileModelName}
                        </div>
                        <div>
                          <strong>อาการเสีย: </strong>
                          {record.breakdownName}
                        </div>
                      </div>
                    </div>
                  ),
                },
                extraContent: (
                  <>
                    <StockSelectionAlert
                      unavailable={isStockUnavailable}
                      overItems={overItems}
                      selectedCount={selectedRowKeys.length}
                      staleMinutes={stockMeta.staleMinutes}
                      onRemoveOverSelected={onRemoveOverSelected}
                    />
                    <SpaceBetweenShowInfo>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        {selectedRowKeys.length > 0
                          ? `เลือก ${selectedRowKeys.length} รายการ`
                          : 'ยังไม่ได้เลือกรายการ'}
                      </div>
                      <ButtonExcelEmbedded
                        title="Export"
                        filename="รายงานการเคลมสินค้า"
                        header={exportHeaders}
                        data={transformDataForExcel(
                          filteredDataSource,
                        )}
                      />
                    </SpaceBetweenShowInfo>
                  </>
                ),
              }}
            />
          </StyledStockTable>
        </CardTheme>
        {openTracking && (
          <ChangeTrackNumber
            open={openTracking}
            onToggle={onToggleTracking}
            claimData={claimData}
            onFetch={onFetch}
            onFetchClaimFull={onFetchClaimFull}
            searchType={searchType}
          />
        )}
        {openLineMessage && (
          <LineMessage
            open={openLineMessage}
            onToggle={onToggleLineMessage}
            claimData={claimData}
            onFetch={onFetch}
          />
        )}
        {openRemarkMessage && (
          <RemarkMessage
            open={openRemarkMessage}
            onToggle={onToggleRemarkMessage}
            claimData={claimData}
            onFetch={onFetch}
            onFetchClaimFull={onFetchClaimFull}
            searchType={searchType}
          />
        )}
        {openProcessStatus && (
          <ProcessStatus
            open={openProcessStatus}
            onToggle={onToggleProcessStatus}
            claimData={claimData}
            onFetch={onFetch}
          />
        )}
      </StyledContainer>
    </Spin>
  );
};
